'use client';

import * as React from 'react';
import { styled } from '@linaria/react';
import { animated, useSpring } from 'react-spring';

import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

import {
  TINKERSYNTH_BULGE_BREAKPOINT,
  TINY_MOBILE_BREAKPOINT,
} from '../AboutJosh.constants';
import FlippableValue from './FlippableValue';
import BaseChild from './BaseChild';

const IMPERIAL_COUNTRIES: Record<string, true> = {
  US: true,
  GB: true,
  CA: true,
  LR: true,
  MM: true,
};

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  country: string | null;
}

function CardTall({ country, ...delegated }: Props) {
  const [isHoveringCard, setIsHoveringCard] = React.useState<
    boolean | null
  >(null);
  const [unit, setUnit] = React.useState<'metric' | 'imperial'>(
    () => {
      return IMPERIAL_COUNTRIES[country || '']
        ? 'imperial'
        : 'metric';
    }
  );

  return (
    <Wrapper
      {...delegated}
      onMouseEnter={() => setIsHoveringCard(true)}
      onMouseLeave={() => setIsHoveringCard(false)}
      onClick={() =>
        setUnit(unit === 'imperial' ? 'metric' : 'imperial')
      }
    >
      <StretchyGuy isStretched={isHoveringCard} />
      <div>
        <p>
          <strong style={{ display: 'block' }}>
            I'm{' '}
            <FlippableValue
              unit={unit}
              imperial="6’2”"
              metric="188cm"
              metricWidth={49.5}
              imperialWidth={34}
            />{' '}
            tall.
          </strong>{' '}
          For some reason, this surprises a lot of people. 😅
        </p>
      </div>
    </Wrapper>
  );
}

function StretchyGuy({
  isStretched,
}: {
  isStretched: boolean | null;
}) {
  const [isFlailingArms, setIsFlailingArms] =
    React.useState<boolean>(false);

  React.useEffect(() => {
    if (isStretched === null) {
      return;
    }

    setIsFlailingArms(true);

    const timeoutId = setTimeout(() => {
      setIsFlailingArms(false);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isStretched]);

  const prefersReducedMotion = usePrefersReducedMotion();

  const config = isStretched
    ? {
        tension: 300,
        friction: 22,
      }
    : {
        tension: 300,
        friction: 12,
      };

  const circleProps = useSpring({
    cx: 25,
    cy: isStretched ? 15 : 35,
    r: 15,
    config,
    immediate: prefersReducedMotion,
  });
  const bodyProps = useSpring({
    x1: 25,
    y1: isStretched ? 30 : 50,
    x2: 25,
    y2: isStretched ? 70 : 80,
    config,
    immediate: prefersReducedMotion,
  });

  const leftLegProps = useSpring({
    x1: 25,
    y1: isStretched ? 70 : 80,
    x2: 15,
    y2: 100,
    config,
    immediate: prefersReducedMotion,
  });
  const rightLegProps = useSpring({
    x1: 25,
    y1: isStretched ? 70 : 80,
    x2: 35,
    y2: 100,
    config,
    immediate: prefersReducedMotion,
  });

  const leftArmProps = useSpring({
    x1: 25,
    y1: isStretched ? 35 : 55,
    // prettier-ignore
    x2: isStretched
      ? (isFlailingArms && !prefersReducedMotion) ? 20 : 15
      : (isFlailingArms && !prefersReducedMotion) ? 5 : 15,

    // prettier-ignore
    y2: isStretched
      ? (isFlailingArms && !prefersReducedMotion) ? 65 : 60
      : (isFlailingArms && !prefersReducedMotion) ? 50 : 70,
    config,
    immediate: prefersReducedMotion,
  });
  const rightArmProps = useSpring({
    x1: 25,
    y1: isStretched ? 35 : 55,
    // prettier-ignore
    x2: isStretched
      ? (isFlailingArms && !prefersReducedMotion) ? 30 : 35
      : (isFlailingArms && !prefersReducedMotion) ? 45 : 35,

    // prettier-ignore
    y2: isStretched
      ? (isFlailingArms && !prefersReducedMotion) ? 65 : 60
      : (isFlailingArms && !prefersReducedMotion) ? 50 : 70,
    config,
    immediate: prefersReducedMotion,
  });

  return (
    <Svg
      width={50}
      height={100}
      viewBox="0 0 50 100"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
    >
      <g
        stroke="var(--color-text)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <Head {...circleProps} />

        {/* Body */}
        <animated.line {...bodyProps} />

        {/* Legs */}
        <animated.line {...leftLegProps} />
        <animated.line {...rightLegProps} />

        {/* Arms */}
        <animated.line {...leftArmProps} />
        <animated.line {...rightArmProps} />
      </g>
    </Svg>
  );
}

const Wrapper = styled(BaseChild)`
  display: flex;
  /* Weirdly, on Safari, links in other cards will flicker when FlippableValue is flipped. Using the Containment API appears to fix this */
  contain: content;

  &:has(button:focus-visible) {
    outline: 2px solid var(--color-primary);
  }

  @media (max-width: ${TINKERSYNTH_BULGE_BREAKPOINT}rem) {
    flex-direction: column;
    align-items: center;
  }
  /* In single-column mobile, the cat ears encroach */
  @media (max-width: ${TINY_MOBILE_BREAKPOINT}rem) {
    padding-bottom: 32px;
  }
`;

const Svg = styled.svg`
  display: block;
  overflow: visible;
  /* Optical alignment */
  transform: translateY(-6px);

  @media (max-width: ${TINKERSYNTH_BULGE_BREAKPOINT}rem) {
    margin-top: -16px;
    margin-bottom: 16px;
  }
`;

const Head = styled(animated.circle)`
  fill: var(--card-bg);
  transition: fill var(--color-swap-duration);
`;

export default CardTall;
